import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "../../lib/api";
import { Mail, X, Send, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const isEmail = (v) => typeof v === "string" && /.+@.+\..+/.test(v);

/**
 * Modal dialog: email the branded SREO (or combined-SREO+rent-roll) PDF to a
 * list of lender recipients. Pre-populates recipients from /user/settings
 * `sreo_lender_emails`. Offers a "Save as default" checkbox so the user
 * doesn't re-enter the same addresses every quarter.
 *
 * Props:
 *   - endpoint  override API path (defaults to "/reports/sreo/email")
 *   - title     override dialog title (defaults to "Email SREO to lender")
 *   - subtitle  override subtitle copy
 */
export default function EmailSreoDialog({
  open,
  onClose,
  endpoint = "/reports/sreo/email",
  title = "Email SREO to lender",
  subtitle = "Attaches the branded PDF · honors your uploaded logo",
  showRentRollToggle = false,
}) {
  const [recipients, setRecipients] = useState([]);
  const [input, setInput] = useState("");
  const [note, setNote] = useState("");
  const [saveAsDefault, setSaveAsDefault] = useState(true);
  const [includeRentRoll, setIncludeRentRoll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingSaved(true);
    api
      .get("/user/settings")
      .then((r) => {
        setRecipients(r.data.sreo_lender_emails || []);
        setNote("");
        setInput("");
        setSaveAsDefault(true);
        setIncludeRentRoll(false);
      })
      .finally(() => setLoadingSaved(false));
  }, [open]);

  if (!open) return null;

  const addRecipient = () => {
    const v = input.trim().toLowerCase();
    if (!isEmail(v)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (recipients.includes(v)) {
      toast.error("Already on the list");
      return;
    }
    setRecipients([...recipients, v]);
    setInput("");
  };

  const removeRecipient = (email) =>
    setRecipients(recipients.filter((e) => e !== email));

  const send = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one lender email before sending");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, {
        recipients,
        note: note.trim() || null,
        save_as_default: saveAsDefault,
        include_rent_roll: showRentRollToggle && includeRentRoll,
      });
      const result = data.result || {};
      const sentLabel = data.packet_mode ? "Lender packet" : "SREO";
      if (result.status === "sent") {
        toast.success(`${sentLabel} emailed to ${data.recipients.length} recipient(s)`, {
          description: data.recipients.join(", "),
        });
      } else if (result.status === "mocked") {
        toast.message("Email in MOCK mode", {
          description: `Would send to: ${data.recipients.join(", ")}. Set RESEND_API_KEY to deliver.`,
        });
      } else if (result.status === "error") {
        toast.error("Email send failed", { description: result.error });
      } else {
        toast.message("Email queued", { description: JSON.stringify(result) });
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      data-testid="email-sreo-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="bg-white border border-slate-200 rounded-sm w-full max-w-xl shadow-xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-indigo-600 text-white flex items-center justify-center rounded-sm shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
                {title}
              </div>
              <div className="text-sm text-slate-500">
                {subtitle}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-slate-400 hover:text-slate-900 p-1 disabled:opacity-40"
            data-testid="email-sreo-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Recipients input */}
          <div>
            <label className="overline text-slate-600">Lender recipients</label>
            <div className="flex gap-2 mt-2">
              <input
                type="email"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder="loanofficer@bank.com"
                className="input-clean flex-1"
                data-testid="email-sreo-input"
              />
              <button
                type="button"
                onClick={addRecipient}
                className="btn-outline-dark flex items-center gap-1.5 text-sm"
                data-testid="email-sreo-add-btn"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>

            {loadingSaved ? (
              <div className="text-xs text-slate-400 font-mono mt-3">Loading saved list…</div>
            ) : recipients.length === 0 ? (
              <div className="mt-3 text-sm text-slate-400 border border-dashed border-slate-200 p-4 text-center rounded-sm">
                No recipients yet. Add at least one lender address.
              </div>
            ) : (
              <ul
                className="mt-3 border border-slate-200 rounded-sm divide-y divide-slate-100"
                data-testid="email-sreo-recipients"
              >
                {recipients.map((e) => (
                  <li
                    key={e}
                    className="px-3 py-2 flex items-center justify-between text-sm"
                    data-testid={`email-sreo-recipient-${e}`}
                  >
                    <span className="font-mono text-slate-700 truncate">{e}</span>
                    <button
                      type="button"
                      onClick={() => removeRecipient(e)}
                      className="text-slate-400 hover:text-red-600 p-1"
                      data-testid={`email-sreo-remove-${e}`}
                      aria-label={`Remove ${e}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="overline text-slate-600">Message to lender (optional)</label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Q1 2026 SREO for the MFM covenant review — let me know if you need anything else."
              className="input-clean mt-2 w-full resize-none"
              data-testid="email-sreo-note"
            />
          </div>

          {/* Lender Packet toggle — attach current-month rent roll as 2nd PDF */}
          {showRentRollToggle && (
            <label
              className={`flex items-start gap-2 cursor-pointer select-none border rounded-sm p-3 transition-colors ${
                includeRentRoll
                  ? "border-emerald-300 bg-emerald-50/60"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              data-testid="email-sreo-include-rent-roll"
            >
              <input
                type="checkbox"
                checked={includeRentRoll}
                onChange={(e) => setIncludeRentRoll(e.target.checked)}
                className="mt-0.5"
                data-testid="email-sreo-include-rent-roll-checkbox"
              />
              <span className="text-xs text-slate-700 leading-snug">
                <strong className="text-slate-900">Attach current-month Rent Roll</strong>{" "}
                <span className="inline-block ml-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-emerald-600 text-white rounded-sm align-middle">
                  Lender Packet
                </span>
                <br />
                <span className="text-slate-500">
                  Adds a second landscape PDF with live expected vs collected vs outstanding by tenant. Lenders get a complete underwriting story (cost basis + collections) in one email.
                </span>
              </span>
            </label>
          )}

          {/* Save-as-default toggle */}
          <label
            className="flex items-start gap-2 cursor-pointer select-none"
            data-testid="email-sreo-save-default"
          >
            <input
              type="checkbox"
              checked={saveAsDefault}
              onChange={(e) => setSaveAsDefault(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-slate-600 leading-snug">
              <strong className="text-slate-900">Save this list as my default.</strong> Next time
              you open this dialog, these recipients will pre-populate so you don't have to retype
              them every quarter.
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn-outline-dark text-sm disabled:opacity-60"
            data-testid="email-sreo-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={busy || recipients.length === 0}
            className="btn-primary-dark flex items-center gap-2 text-sm disabled:opacity-60"
            data-testid="email-sreo-send-btn"
          >
            <Send className="w-4 h-4" />
            {busy
              ? "Sending…"
              : showRentRollToggle && includeRentRoll
              ? `Send Packet to ${recipients.length || 0}`
              : `Send to ${recipients.length || 0}`}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
